import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { addBranchFilter } from '../utils/branchFilter';
import './Products.css';

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('latest');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // default to grid view
  const productsStartRef = useRef(null);
  
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch only Punjab branch products
        const params = addBranchFilter({});
        const response = await axios.get(`${config.API_URL}/products`, { params });
        setProducts(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load products');
        setLoading(false);
        console.error('Error fetching products:', err);
      }
    };

    fetchProducts();
  }, []);

  // Category configuration with modern icons
  const categoryConfig = {
    'mobiles': {
      name: 'Mobiles',
      icon: '📱',
      color: '#007bff',
      bgColor: '#e7f3ff',
      keywords: ['mobile', 'phone', 'smartphone', 'iphone', 'samsung galaxy', 'oneplus', 'xiaomi', 'oppo', 'vivo', 'realme', 'motorola', 'nokia', 'itel', 'lava', 'infinix', 'tecno', 'redmi', 'poco']
    },
    'old-mobiles': {
      name: 'Old Mobiles',
      icon: '📲',
      color: '#6c757d',
      bgColor: '#f8f9fa',
      keywords: ['old mobile', 'used mobile', 'refurbished', 'second hand phone']
    },
    'tvs-appliances': {
      name: 'TVs & Appliances',
      icon: '📺',
      color: '#28a745',
      bgColor: '#e8f5e8',
      keywords: ['tv', 'television', 'led', 'smart tv', 'samsung tv', 'lg tv', 'sony tv', 'refrigerator', 'fridge', 'washing machine', 'ac', 'air conditioner', 'microwave', 'oven', 'mixer', 'juicer', 'grinder', 'fan', 'cooler']
    },
    'home-audio': {
      name: 'Home Theatre & Speakers',
      icon: '🔊',
      color: '#20c997',
      bgColor: '#e6fffa',
      keywords: ['home theatre', 'home theater', 'speaker', 'speakers', 'soundbar', 'woofer', 'subwoofer', 'home audio']
    },
    'laptops': {
      name: 'Laptops & Computers',
      icon: '💻',
      color: '#6f42c1',
      bgColor: '#f3e8ff',
      keywords: ['laptop', 'computer', 'desktop', 'pc', 'macbook', 'dell', 'hp', 'lenovo', 'asus']
    },
    'accessories': {
      name: 'Accessories',
      icon: '🎧',
      color: '#fd7e14',
      bgColor: '#fff3e0',
      keywords: ['headphone', 'earphone', 'charger', 'cable', 'case', 'cover', 'power bank', 'bluetooth']
    },
    'others': {
      name: 'Others',
      icon: '🛍️',
      color: '#dc3545',
      bgColor: '#ffeaea',
      keywords: []
    }
  };

  // Categorize products automatically
  const categorizeProduct = (product) => {
    const productName = product.name?.toLowerCase() || '';
    const productCategory = product.category?.toLowerCase() || '';
    const productDescription = product.description?.toLowerCase() || '';
    const searchText = `${productName} ${productCategory} ${productDescription}`;

    // First, check the actual product category from database
    if (productCategory) {
      // Map database categories to display categories
      if (['tv', 'television'].includes(productCategory)) {
        return 'tvs-appliances';
      }
      if (['refrigerator', 'fridge', 'washing machine', 'ac', 'air conditioner', 'microwave', 'oven', 'mixer/juicer', 'grinder', 'fan', 'cooler'].includes(productCategory)) {
        return 'tvs-appliances';
      }
      if (['home theatre', 'home theater', 'speaker', 'speakers', 'soundbar'].includes(productCategory)) {
        return 'home-audio';
      }
      if (['old mobile', 'used mobile', 'refurbished'].includes(productCategory)) {
        return 'old-mobiles';
      }
      if (['laptop', 'computer', 'desktop', 'pc'].includes(productCategory)) {
        return 'laptops';
      }
      if (['headphone', 'earphone', 'neckband', 'airpods', 'smartwatches', 'charger', 'cable', 'case', 'cover', 'power bank', 'bluetooth'].includes(productCategory)) {
        return 'accessories';
      }
      if (['mobile', 'phone', 'smartphone'].includes(productCategory)) {
        return 'mobiles';
      }
    }

    // If no category or category not recognized, use name-based detection
    // Check for TVs and appliances (be more specific)
    if (['tv', 'television', 'led tv', 'smart tv'].some(k => searchText.includes(k)) && !searchText.includes('mobile')) {
      return 'tvs-appliances';
    }
    if (['refrigerator', 'fridge', 'washing machine', 'ac', 'air conditioner', 'microwave', 'oven', 'mixer', 'juicer', 'grinder', 'fan', 'cooler'].some(k => searchText.includes(k))) {
      return 'tvs-appliances';
    }
    
    // Check for home audio
    if (['home theatre', 'home theater', 'speaker', 'speakers', 'soundbar', 'woofer', 'subwoofer', 'home audio'].some(k => searchText.includes(k))) {
      return 'home-audio';
    }
    
    // Check for old mobiles
    if (['old mobile', 'used mobile', 'refurbished', 'second hand phone'].some(k => searchText.includes(k))) {
      return 'old-mobiles';
    }
    
    // Check for laptops
    if (['laptop', 'computer', 'desktop', 'pc', 'macbook', 'dell', 'hp', 'lenovo', 'asus'].some(k => searchText.includes(k))) {
      return 'laptops';
    }
    
    // Check for accessories
    if (['headphone', 'earphone', 'neckband', 'airpods', 'smartwatches', 'charger', 'cable', 'case', 'cover', 'power bank', 'bluetooth'].some(k => searchText.includes(k))) {
      return 'accessories';
    }
    
    // Check for regular mobiles (last priority)
    if (['mobile', 'phone', 'smartphone', 'iphone', 'samsung galaxy', 'oneplus', 'xiaomi', 'oppo', 'vivo', 'realme', 'motorola', 'nokia', 'itel', 'lava', 'infinix', 'tecno', 'redmi', 'poco'].some(k => searchText.includes(k))) {
      return 'mobiles';
    }

    return 'others';
  };

  // Group products by category
  const getProductsByCategory = () => {
    const categorized = {};
    Object.keys(categoryConfig).forEach(key => {
      categorized[key] = [];
    });

    products.forEach(product => {
      const category = categorizeProduct(product);
      categorized[category].push(product);
    });

    return categorized;
  };

  // Filter products based on search term and category
  const getFilteredProducts = () => {
    let filtered = products;
    
    if (searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
      product.name?.toLowerCase().includes(searchLower) ||
      product.category?.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower)
    );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => categorizeProduct(product) === selectedCategory);
    }
    
    return filtered;
  };

  // Sorting function - handles both filtered products and specific arrays
  const getSortedProducts = (productsArray = null) => {
    const productsToSort = productsArray || getFilteredProducts();
    const sortedProducts = [...productsToSort];
    
    switch (sortBy) {
      case 'latest':
        return sortedProducts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sortedProducts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'priceLowToHigh':
        return sortedProducts.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'priceHighToLow':
        return sortedProducts.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'stockLowToHigh':
        return sortedProducts.sort((a, b) => (a.stock || 0) - (b.stock || 0));
      case 'stockHighToLow':
        return sortedProducts.sort((a, b) => (b.stock || 0) - (a.stock || 0));
      case 'nameAZ':
        return sortedProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'nameZA':
        return sortedProducts.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      default:
        return sortedProducts;
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center">
        <h2>Products</h2>
        <div className="alert alert-info">No products found</div>
        <Link to="/add-product" className="btn btn-primary">Add New Product</Link>
      </div>
    );
  }

  const sortedProducts = getSortedProducts();
  const filteredCount = getFilteredProducts().length;
  const totalCount = products.length;

  return (
    <div>
      <div className="d-flex justify-content-end gap-2 mb-2">
        <Link to="/inventory" className="btn btn-sm btn-info">📋 Table View</Link>
        <Link to="/add-product" className="btn btn-sm btn-success">➕ Add Product</Link>
        </div>

      {/* Quick Category Icon Bar */}
      <div className="category-quickbar mb-3">
        {Object.entries(categoryConfig).map(([key, cfg]) => (
          <button
            key={key}
            type="button"
            className={`quick-item ${selectedCategory === key ? 'active' : ''}`}
            onClick={() => {
              setSelectedCategory(key);
              if (productsStartRef.current) {
                productsStartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            title={cfg.name}
          >
            <span className="quick-icon">{cfg.icon}</span>
            <span className="quick-label">{cfg.name}</span>
          </button>
        ))}
      </div>

      {/* Search and Filter Controls */}
      <div className="row mb-4">
        <div className="col-md-8">
          <div className="search-container">
          <div className="input-group">
              <span className="input-group-text bg-primary text-white">
                🔍
            </span>
            <input
              type="text"
                className="form-control form-control-lg"
                placeholder="Search products, categories, descriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm('')}
              >
                  ✕
              </button>
            )}
          </div>
          {searchTerm && (
              <small className="text-muted mt-1 d-block">
                📊 Showing {filteredCount} of {totalCount} products
            </small>
          )}
        </div>
        </div>
        <div className="col-md-4">
          <select 
            className="form-select form-select-lg"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="latest">🕒 Latest First</option>
            <option value="oldest">🕒 Oldest First</option>
            <option value="priceLowToHigh">💰 Price: Low to High</option>
            <option value="priceHighToLow">💰 Price: High to Low</option>
            <option value="stockLowToHigh">📦 Stock: Low to High</option>
            <option value="stockHighToLow">📦 Stock: High to Low</option>
            <option value="nameAZ">📝 Name: A to Z</option>
            <option value="nameZA">📝 Name: Z to A</option>
          </select>
        </div>
      </div>

      {/* Anchor for smooth scroll when clicking quick category */}
      <div ref={productsStartRef} />

      {/* No results message */}
      {searchTerm && filteredCount === 0 && (
        <div className="alert alert-info text-center border-0 shadow-sm">
          <div className="mb-2">
            <span style={{fontSize: '3rem'}}>🔍</span>
          </div>
          <h5>No products found</h5>
          <p className="mb-3">No products match "{searchTerm}"</p>
          <button 
            className="btn btn-outline-info"
            onClick={() => setSearchTerm('')}
          >
            🔄 Clear Search
          </button>
        </div>
      )}

      {/* Product Grid */}
      <div className="row g-3">
        {getSortedProducts().map(product => renderProductCard(product))}
      </div>
    </div>
  );

  // Product card render function
  function renderProductCard(product, parentConfig = null) {
          const stockClass = product.stock > 10 ? 'high-stock' : (product.stock > 3 ? 'medium-stock' : 'low-stock');
    const category = categorizeProduct(product);
    const config = parentConfig || categoryConfig[category];
          
          // Improved image source logic
          const getImageSrc = () => {
            if (product.imageUrl) {
              return product.imageUrl;
            }
            if (product.images && product.images.length > 0) {
              return product.images[0];
            }
            return 'https://placehold.co/300x200/e9ecef/495057?text=No+Image';
          };
          
          const imageSrc = getImageSrc();
    const isOldMobile = category === 'old-mobiles';
          
          return (
            <div className="product-grid-item col-6 col-sm-6 col-md-4 col-lg-3 col-xl-2" key={product._id}>
        <div className={`card product-card h-100 ${isOldMobile ? 'old-mobile-card' : ''}`}>
          {product.emiAvailable && (
            <span className="badge bg-success emi-badge">
              💳 EMI
            </span>
          )}
          {isOldMobile && (
            <span className="badge bg-warning old-mobile-badge" style={{top: '10px', left: '10px', position: 'absolute', zIndex: 10}}>
              💎 Premium
            </span>
          )}
          <div className="position-relative">
                <img 
                  src={imageSrc} 
                  className="card-img-top product-image p-2" 
                  alt={product.name}
              key={product.imageUrl || product.images?.[0]}
                  onError={(e) => {
                    e.target.src = 'https://placehold.co/300x200/f8d7da/721c24?text=Image+Error';
                  }}
                />
            {config && (
              <div 
                className="category-indicator position-absolute"
                style={{
                  bottom: '10px',
                  right: '10px',
                  backgroundColor: config.color,
                  color: 'white',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  boxShadow: `0 2px 8px ${config.color}40`
                }}
              >
                {config.icon}
              </div>
            )}
          </div>
          <div className="card-body p-3">
            <h6 className="card-title mb-2 product-title">{product.name}</h6>
                  <p className="card-text small mb-2">
              <span className="text-muted">
                {categoryConfig?.[category]?.icon} {product.category}
              </span>
                  </p>
            <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-primary fs-6">₹{product.price.toLocaleString()}</strong>
                    {product.offerPrice && (
                      <span className="text-success small">
                        <strong>₹{product.offerPrice.toLocaleString()}</strong>
                      </span>
                    )}
                  </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className={`small ${stockClass} fw-bold`}>📦 {product.stock} in stock</span>
              {isOldMobile && (
                <span className="badge bg-warning text-dark">EMI Ready</span>
              )}
            </div>
                </div>
          <div className="card-footer d-flex justify-content-between align-items-center">
            <Link to={`/product/${product._id}`} className="btn btn-sm btn-primary flex-grow-1 me-2">
              👁️ View Details
                  </Link>
            {isOldMobile && (
              <button className="btn btn-sm btn-outline-warning">
                💳
              </button>
            )}
                </div>
              </div>
            </div>
          );
  }

}

export default Products; 